import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { branding } from './uiStyles';
import { resolveTenantLogoUrl } from './utils/branding/resolveTenantLogoUrl';
import { useAuth } from './AuthContext';
import { ensureFreshSession } from './utils/auth/authSessionSyncHelpers';
import useAssuranceSettled from './hooks/auth/useAssuranceSettled';
import useDashboardRefreshExtra from './hooks/ui/useDashboardRefreshExtra';
import {
  getCompanySettings,
  normalizeCompanySettings,
  normalizeCompanySettingsError,
  saveCompanySettings,
  saveMfaRequiredRoles as saveMfaRequiredRolesService,
} from './services/companySettingsService';
import { resetBrandingDefaults } from './services/settings/companySettingsShape';
import { savePriceVisibleFieldRoles as savePriceVisibleFieldRolesService } from './services/companySettingsPriceVisibility';
import { setPriceVisibleFieldRoles as setPriceVisibleFieldRolesStore } from './utils/auth/priceVisibilityStore';
import useFeatureChangePing from './hooks/tenant/useFeatureChangePing';
import { notifyCompanyPlanStale } from './utils/plan/companyPlanBus';
import { setCalendarTenantTimezone } from './utils/date/calendarZone';
import { runAfterAuthHandoff } from './ui/splash/authHandoffHold';

const CompanySettingsContext = createContext({
  settings: normalizeCompanySettings(),
  mfaRequiredRoles: [],
  mfaFloorRoles: [],
  priceVisibleFieldRoles: ['supervisor'],
  loading: true,
  refreshSettings: async () => ({ data: normalizeCompanySettings(), error: null }),
  saveSettings: async () => ({ data: normalizeCompanySettings(), error: null }),
  saveMfaRequiredRoles: async () => ({ data: null, error: null }),
  savePriceVisibleFieldRoles: async () => ({ data: null, error: null }),
});

// The shared `branding` feeds every PDF / preview / report logo (getInvoiceLogoSrc),
// so the logo is host- and tenant-pinned HERE, once: anything that is not this
// tenant's own branding-assets raster on our Supabase origin becomes null.
// (The `settings` state keeps the raw value so the Branding form can show and clear it.)
const applyBrandingSettings = (settings, forTenantId) => {
  Object.assign(branding, settings);
  if (settings && 'companyLogoUrl' in settings) {
    branding.companyLogoUrl = resolveTenantLogoUrl(settings.companyLogoUrl, forTenantId);
  }

  if (typeof document !== 'undefined' && settings?.appName) {
    document.title = settings.appName;
  }
};

export function CompanySettingsProvider({ children }) {
  const { tenantId, tenantLoading, sessionUser, isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState(() => normalizeCompanySettings());
  const [mfaRequiredRoles, setMfaRequiredRoles] = useState([]);
  const [mfaFloorRoles, setMfaFloorRoles] = useState([]);
  const [priceVisibleFieldRoles, setPriceVisibleFieldRolesState] = useState(['supervisor']);
  const [loading, setLoading] = useState(true);
  // Tenant whose settings were last read SUCCESSFULLY (null after sign-out,
  // a tenant switch, or before the first good read). Lets a consumer tell real
  // settings from the normalized defaults (RD-I18N-006 language write-once).
  const [loadedTenantId, setLoadedTenantId] = useState(null);
  // KAN-590: true while NO good settings were ever read for this tenant (the values
  // above are the safe defaults). Settings tab shows an inline LoadErrorState on it.
  const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);
  // RD-SEC-051: tenant whose last load returned the deferred-empty result (row missing,
  // bootstrap write skipped before 2FA). Reloaded once when assurance settles.
  const deferredTenantRef = useRef(null);
  const goodTenantRef = useRef(null); // tenant whose REAL settings are applied (sync mirror of loadedTenantId)
  /** Invalidates in-flight fetches when tenant/session changes so stale aiEnabled cannot win. */
  const settingsFetchGenRef = useRef(0);
  /** Bumped on every sign-out / tenant change. A write or read that STARTED in an
      earlier session must never apply its result into a later one: `withTimeout`
      abandons a hung save but cannot cancel it, so on a shared device tenant A's
      late save could otherwise land in tenant B's settings. Separate from
      `settingsFetchGenRef`, which a save also bumps (to supersede stale reads). */
  const sessionEpochRef = useRef(0);
  /** Tenant whose settings are currently applied onto the shared `branding`. */
  const appliedTenantRef = useRef(null);
  const snapshotSession = (forTenantId) => ({ epoch: sessionEpochRef.current, tenantId: forTenantId });
  const isSameSession = (snap) => snap.epoch === sessionEpochRef.current
    && (snap.tenantId ?? null) === (appliedTenantRef.current ?? null);
  const sessionChangedResult = () => ({ data: null, error: new Error('SESSION_CHANGED') });
  /** A successful write is authoritative: supersede any refresh already in flight
      (it read BEFORE the write and would resurrect the old row) and clear a
      non-silent load it may have left spinning (see the 2026-08-14 wedge above). */
  const supersedeInFlightReads = () => {
    settingsFetchGenRef.current += 1;
    setLoading(false);
  };

  // RD-ROLE-005b: mirrors every priceVisibleFieldRoles change (and reset)
  // into priceVisibilityStore.js so non-hook (plain module) callers can read
  // it without a hand-threaded param. Same pattern as AuthContext's
  // setRoleMirrored — ONE call site decides the value for both React state
  // and the module-level mirror, so they cannot drift apart.
  const setPriceVisibleFieldRolesMirrored = useCallback((roles) => {
    setPriceVisibleFieldRolesStore(roles);
    setPriceVisibleFieldRolesState(roles);
  }, []);

  // `silent` skips only the loading→TRUE transition (no header-skeleton flash
  // on background resume refreshes). Every fetch that is still the current
  // generation when it settles MUST clear `loading` — including silent ones:
  // a silent refresh can supersede an in-flight cold load (focus/resume fires
  // during login, Org pull-to-refresh), and the superseded cold load
  // early-returns without clearing the flag. If the silent winner skipped the
  // clear too, `loading` stuck true for the whole session (header company-name
  // skeleton, AI FAB hidden, Org tab unsavable — 2026-08-14 wedge; guard:
  // CompanySettingsContext.test.js).
  const refreshSettings = useCallback(async (activeTenantId = tenantId, { silent = false } = {}) => {
    const gen = ++settingsFetchGenRef.current;
    const session = snapshotSession(activeTenantId);
    if (!silent) setLoading(true);
    try {
      const result = await getCompanySettings(activeTenantId);
      if (gen !== settingsFetchGenRef.current || !isSameSession(session)) {
        return result;
      }
      // KAN-589: a failed SILENT re-read (pull / resume) keeps the last good
      // settings — `result.data` is the all-defaults fallback on error.
      // KAN-590: a failed NON-silent read must not apply all-defaults over good settings
      // either; with no good settings yet, flag it so callers can offer a retry.
      if (result.error && (silent || goodTenantRef.current === activeTenantId)) {
        if (goodTenantRef.current !== activeTenantId) setSettingsLoadFailed(true);
        setLoading(false);
        return result;
      }
      setSettingsLoadFailed(Boolean(result.error));
      deferredTenantRef.current = result.deferred ? activeTenantId : null;
      goodTenantRef.current = result.error ? null : activeTenantId;
      applyBrandingSettings(result.data, activeTenantId);
      setSettings(result.data);
      setMfaRequiredRoles(result.data?.mfaRequiredRoles ?? []);
      setMfaFloorRoles(result.data?.mfaFloorRoles ?? []);
      setPriceVisibleFieldRolesMirrored(result.data?.priceVisibleFieldRoles ?? ['supervisor']);
      setLoadedTenantId(result.error ? null : activeTenantId);
      setLoading(false);
      return result;
    } catch (err) {
      console.error('Company settings refresh failed:', err);
      if (gen === settingsFetchGenRef.current) {
        if (goodTenantRef.current !== activeTenantId) setSettingsLoadFailed(true);
        setLoading(false);
      }
      return {
        data: normalizeCompanySettings(),
        error: normalizeCompanySettingsError(err),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, setPriceVisibleFieldRolesMirrored]);

  const updateSettings = useCallback(async (nextSettings, opts) => {
    const session = snapshotSession(tenantId);
    try {
      const result = await saveCompanySettings(nextSettings, tenantId, opts);
      if (!isSameSession(session)) return sessionChangedResult();
      if (!result.error) {
        supersedeInFlightReads();
        applyBrandingSettings(result.data, tenantId);
        // MERGE over the previous settings: the save result carries only the
        // editable keys, so replacing would drop aiEnabled / mfa* / price*
        // (which used to survive only by accident, via the mutated `branding`).
        setSettings((prev) => ({ ...prev, ...result.data }));
      }
      return result;
    } catch (err) {
      return {
        data: normalizeCompanySettings(),
        error: normalizeCompanySettingsError(err),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const updateMfaRequiredRoles = useCallback(async (rolesArray) => {
    const session = snapshotSession(tenantId);
    try {
      const result = await saveMfaRequiredRolesService(rolesArray, tenantId);
      if (!isSameSession(session)) return sessionChangedResult();
      if (!result.error) {
        supersedeInFlightReads();
        setMfaRequiredRoles(result.data ?? []);
        // Every copy the context holds — else the next refresh flips the bar.
        setSettings((prev) => ({ ...prev, mfaRequiredRoles: result.data ?? [] }));
      }
      return result;
    } catch (err) {
      return { data: null, error: normalizeCompanySettingsError(err) };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const updatePriceVisibleFieldRoles = useCallback(async (rolesArray) => {
    const session = snapshotSession(tenantId);
    try {
      const result = await savePriceVisibleFieldRolesService(rolesArray, tenantId);
      if (!isSameSession(session)) return sessionChangedResult();
      if (!result.error) {
        supersedeInFlightReads();
        setPriceVisibleFieldRolesMirrored(result.data ?? []);
        setSettings((prev) => ({ ...prev, priceVisibleFieldRoles: result.data ?? [] }));
      }
      return result;
    } catch (err) {
      return { data: null, error: normalizeCompanySettingsError(err) };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, setPriceVisibleFieldRolesMirrored]);

  /**
   * Depend on the user *id* rather than the sessionUser reference: Supabase emits
   * fresh user objects on TOKEN_REFRESHED / focus-driven re-syncs even when the
   * signed-in user has not changed. Re-running this effect on every such ping
   * caused a redundant company_settings fetch and a loading-skeleton flash on
   * the centered tenant-name button in the shell header.
   */
  const sessionUserId = sessionUser?.id ?? null;
  // Date pickers read "today" in the company zone (KAN-791); unknown until a good read.
  const calendarTz = loadedTenantId ? settings?.timezone : null;
  useEffect(() => {
    setCalendarTenantTimezone(calendarTz);
    return () => setCalendarTenantTimezone(null);
  }, [calendarTz]);
  const { settled: assuranceSettled, aal: sessionAal } = useAssuranceSettled(sessionUserId);
  useEffect(() => {
    if (tenantLoading) {
      setLoading(true);
      return undefined;
    }

    if (!sessionUserId) {
      settingsFetchGenRef.current += 1;
      sessionEpochRef.current += 1; // a save started before sign-out must not land in the next session
      // Shared device: never leave the previous tenant's values on `branding`.
      resetBrandingDefaults();
      appliedTenantRef.current = null;
      setLoading(false);
      setSettings({ ...normalizeCompanySettings(), aiEnabled: false, features: {} });
      // RD-ROLE-005b: without this, priceVisibleFieldRoles (state AND the
      // store mirror) survived sign-out — on a shared device, tenant A's
      // setting would leak into tenant B's session until refreshSettings
      // resolved. mfaRequiredRoles/mfaFloorRoles have the same gap; out of
      // scope here (not price-visibility related).
      setPriceVisibleFieldRolesMirrored(['supervisor']);
      setMfaRequiredRoles([]);
      setMfaFloorRoles([]);
      setLoadedTenantId(null);
      goodTenantRef.current = null;
      deferredTenantRef.current = null; // KAN-676: a deferred (pre-2FA, no row) load never outlives its session/tenant
      setSettingsLoadFailed(false);
      return undefined;
    }

    if (appliedTenantRef.current && appliedTenantRef.current !== tenantId) {
      // Tenant switch: drop the old tenant's values NOW — branding, the settings
      // state and the role state/mirror — so no frame shows tenant A's data.
      sessionEpochRef.current += 1;
      resetBrandingDefaults();
      setSettings({ ...normalizeCompanySettings(), aiEnabled: false, features: {} });
      setPriceVisibleFieldRolesMirrored(['supervisor']);
      setMfaRequiredRoles([]);
      setMfaFloorRoles([]);
      setLoadedTenantId(null);
      goodTenantRef.current = null;
      deferredTenantRef.current = null; // KAN-676: a deferred (pre-2FA, no row) load never outlives its session/tenant
      setSettingsLoadFailed(false);
    }
    // Nothing but the 2FA decision runs before the 2FA code: no first read until
    // assurance settles (an aal1 read of a 2FA-enrolled user is empty under RLS anyway).
    if (!assuranceSettled) return undefined;
    appliedTenantRef.current = tenantId;
    // The first read waits out a sign-in handoff (authHandoffHold.js); `loading`
    // is already true, so nothing on screen changes meanwhile.
    return runAfterAuthHandoff(() => {
      void refreshSettings(tenantId).catch((err) => {
        console.error('Company settings refresh failed:', err);
        setLoading(false);
      });
    });
  }, [refreshSettings, tenantId, tenantLoading, sessionUserId, assuranceSettled, setPriceVisibleFieldRolesMirrored]);

  // RD-SEC-051: a deferred-empty first load (no row yet, write skipped before 2FA)
  // loads again once assurance settles; a real row is never refetched here.
  useEffect(() => {
    if (!assuranceSettled || !sessionUserId || tenantLoading || !tenantId) return;
    if (deferredTenantRef.current !== tenantId) return;
    void refreshSettings(tenantId, { silent: true });
  }, [assuranceSettled, sessionAal, sessionUserId, tenantId, tenantLoading, refreshSettings]);

  // Silent resume refresh: an already-open session loads company settings only
  // on login/tenant-change, so a platform-side change (notably the MFA floor)
  // wouldn't reach it until a full reload. On app foreground re-read silently
  // (no skeleton flash) so the Security panel reflects the change within
  // seconds. Throttled to keep rapid tab-switches cheap; ensureFreshSession
  // first so the read carries a valid token (iOS frozen-token footgun).
  // KAN-589: every role's pull refresh also re-reads company settings, silently.
  // Forms seeded from settings carry pristine-only reseed guards (see
  // useBrandingPullRefresh.js), the same protection the resume refresh below relies on.
  useDashboardRefreshExtra(
    () => refreshSettings(tenantId, { silent: true }),
    Boolean(sessionUserId && tenantId && !tenantLoading),
    // manualOnly: the throttled resume read below already covers silent resumes.
    { manualOnly: true },
  );
  const lastResumeRefreshRef = useRef(0);
  useEffect(() => {
    const RESUME_MIN_INTERVAL_MS = 30000;
    const maybeRefresh = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!sessionUserId || tenantLoading || !tenantId) return;
      const now = Date.now();
      if (now - lastResumeRefreshRef.current < RESUME_MIN_INTERVAL_MS) return;
      lastResumeRefreshRef.current = now;
      await ensureFreshSession();
      void refreshSettings(tenantId, { silent: true });
    };
    const onVisible = () => { void maybeRefresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [sessionUserId, tenantId, tenantLoading, refreshSettings]);

  // KAN-731: a CrewVex-side feature switch reaches every open tenant session within
  // seconds (data-free ping → silent re-read; bypasses the 30s resume throttle above,
  // which stays as the backup). Never for platform (no tenant, no pings).
  useFeatureChangePing({
    tenantId,
    // Waits for the 2FA step to settle (KAN-676): an aal1 subscription reads nothing under RLS.
    enabled: Boolean(sessionUserId && tenantId && !tenantLoading && !isSuperAdmin && assuranceSettled),
    // KAN-728: the same ping also refreshes the company's Plan & billing + AI heads-up (one store).
    onPing: () => { void refreshSettings(tenantId, { silent: true }); notifyCompanyPlanStale(); },
  });

  const value = useMemo(() => ({
    settings,
    mfaRequiredRoles,
    mfaFloorRoles,
    priceVisibleFieldRoles,
    loading,
    loadedTenantId,
    settingsLoadFailed,
    refreshSettings,
    saveSettings: updateSettings,
    saveMfaRequiredRoles: updateMfaRequiredRoles,
    savePriceVisibleFieldRoles: updatePriceVisibleFieldRoles,
  }), [
    loadedTenantId, settingsLoadFailed, loading, mfaFloorRoles, mfaRequiredRoles, priceVisibleFieldRoles, refreshSettings, settings,
    updateMfaRequiredRoles, updatePriceVisibleFieldRoles, updateSettings,
  ]);

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  );
}

export const useCompanySettings = () => useContext(CompanySettingsContext);